import type { ModoVivoEvent } from '@modo-vivo/contracts';

export type EventHandler = (event: ModoVivoEvent) => void | Promise<void>;

/**
 * Cola FIFO con procesamiento secuencial y deduplicación por id.
 */
export class EventProcessor {
  private queue: ModoVivoEvent[] = [];
  private seen = new Set<string>();
  private processing = false;
  private handlers: Partial<Record<ModoVivoEvent['type'], EventHandler[]>> = {};
  private onProcessed?: (event: ModoVivoEvent) => void;

  constructor(onProcessed?: (event: ModoVivoEvent) => void) {
    this.onProcessed = onProcessed;
  }

  on(type: ModoVivoEvent['type'], handler: EventHandler): void {
    const list = this.handlers[type] ?? [];
    list.push(handler);
    this.handlers[type] = list;
  }

  enqueue(event: ModoVivoEvent): void {
    if (this.seen.has(event.id)) {
      return;
    }
    this.seen.add(event.id);
    this.queue.push(event);
    void this.drain();
  }

  enqueueMany(events: ModoVivoEvent[]): void {
    for (const event of events) {
      this.enqueue(event);
    }
  }

  replay(events: ModoVivoEvent[]): void {
    this.seen.clear();
    this.queue = [];
    this.enqueueMany(events);
  }

  private async drain(): Promise<void> {
    if (this.processing) {
      return;
    }
    this.processing = true;
    while (this.queue.length > 0) {
      const event = this.queue.shift()!;
      const handlers = this.handlers[event.type] ?? [];
      for (const handler of handlers) {
        await handler(event);
      }
      this.onProcessed?.(event);
    }
    this.processing = false;
  }
}
